import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query'

import { toast } from '@/components/feedback/toast'
import { normalizeApiError } from '@/lib/apiEnvelope'

import { minhasVisitasApi } from '../../shared/api/visitasApi'
import { aplicarDetalhe } from '../../shared/hooks/useVisitas'
import { operacionalKeys } from '../../shared/hooks/operacionalKeys'
import type { VisitaStatus } from '../../shared/types/enums'
import type {
  EtapaUpdatePayload,
  FotoUploadPayload,
  VisitaDetalhe,
} from '../../shared/types/visita'

export interface MinhasVisitasFilters {
  dataInicio?: string
  dataFim?: string
  status?: VisitaStatus
  page: number
  pageSize: number
}

const aplicar = (visita: VisitaDetalhe) =>
  aplicarDetalhe(operacionalKeys.minhas.detail(visita.id), visita)

export function useMinhasVisitas(filters: MinhasVisitasFilters, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: operacionalKeys.minhas.list(filters),
    queryFn: () => minhasVisitasApi.list(filters),
    placeholderData: keepPreviousData,
    enabled: options?.enabled ?? true,
  })
}

/**
 * `true` quando o usuário é responsável por alguma visita em aberto — decide se
 * "Visitas" aparece na barra inferior do celular. Erro (ex.: 403) = não mostra.
 */
export function useTemVisitasAbertas(): boolean {
  const { data } = useQuery({
    queryKey: operacionalKeys.minhas.list({ page: 1, pageSize: 1 }),
    queryFn: () => minhasVisitasApi.list({ page: 1, pageSize: 1 }),
    staleTime: 5 * 60_000,
    retry: false,
  })
  return (data?.totalCount ?? 0) > 0
}

export function useMinhaVisita(id: string | undefined) {
  return useQuery({
    queryKey: operacionalKeys.minhas.detail(id ?? ''),
    queryFn: () => minhasVisitasApi.getById(id as string),
    enabled: !!id,
  })
}

const erroToast = (error: unknown) => toast.error(normalizeApiError(error).detail)

export function useIniciarVisita() {
  return useMutation({
    mutationFn: (id: string) => minhasVisitasApi.iniciar(id),
    onSuccess: (visita) => {
      void aplicar(visita)
      toast.success('Check-in feito. Bom trabalho!')
    },
    onError: erroToast,
  })
}

/** Sem toast de erro: a tela da etapa mostra o erro junto da foto (com "tentar de novo"). */
export function useEnviarFoto() {
  return useMutation({
    mutationFn: ({
      id,
      etapaId,
      payload,
    }: {
      id: string
      etapaId: string
      payload: FotoUploadPayload
    }) => minhasVisitasApi.enviarFoto(id, etapaId, payload),
    onSuccess: (visita) => void aplicar(visita),
  })
}

export function useRemoverFoto() {
  return useMutation({
    mutationFn: ({ id, fotoId }: { id: string; fotoId: string }) =>
      minhasVisitasApi.removerFoto(id, fotoId),
    onSuccess: (visita) => void aplicar(visita),
    onError: erroToast,
  })
}

/** Sem toast: a tela da etapa mostra o erro no formulário. */
export function useAtualizarEtapa() {
  return useMutation({
    mutationFn: ({
      id,
      etapaId,
      payload,
    }: {
      id: string
      etapaId: string
      payload: EtapaUpdatePayload
    }) => minhasVisitasApi.atualizarEtapa(id, etapaId, payload),
    onSuccess: (visita) => void aplicar(visita),
  })
}

/** O `MotivoDialog` exibe o erro. */
export function usePausarVisita() {
  return useMutation({
    mutationFn: ({ id, motivo }: { id: string; motivo: string }) =>
      minhasVisitasApi.pausar(id, motivo),
    onSuccess: (visita) => {
      void aplicar(visita)
      toast.info('Visita pausada. Retome quando puder.')
    },
  })
}

export function useRetomarVisita() {
  return useMutation({
    mutationFn: (id: string) => minhasVisitasApi.retomar(id),
    onSuccess: (visita) => void aplicar(visita),
    onError: erroToast,
  })
}

/** Sem toast de erro: as pendências (400 `Etapas`) aparecem na tela de execução. */
export function useFinalizarVisita() {
  return useMutation({
    mutationFn: (id: string) => minhasVisitasApi.finalizar(id),
    onSuccess: (visita) => {
      void aplicar(visita)
      toast.success('Visita finalizada e enviada para revisão.')
    },
  })
}
